import NProgress from "nprogress";
import AppRoutes from "./custom/AppRoutes";
import { PATH_APP, PATH_HOME, PATH_PAGE } from "./paths";
import LoadingScreen from "src/components/LoadingScreen";
import GuestProtect from "src/components/Auth/GuestProtect";
import AuthProtect from "src/components/Auth/AuthProtect";
import { Switch, Route, Redirect } from "react-router-dom";
import React, { Suspense, Fragment, lazy, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import NoAuthorizedSessionProtect from "src/components/Auth/NoAuthorizedSessionProtect";
import SessionRoutes from "./custom/SessionRoutes";
import HomeLayout from "src/layouts/HomeLayout";
import UserRoutes from "./custom/UserRoutes";
import RoleBasedGuard from "src/components/Auth/RoleBasedGuard";
import RequireAction from "src/components/Auth/RequireAction";
import { getRouteGate } from "./actionByRoute";
import TpvRoutes from "./custom/TpvRoutes";
import TpvCartaRoutes from "./custom/TpvCartaRoutes";
import AdminRoutes from "./custom/AdminRoutes";

// ----------------------------------------------------------------------

const nprogressStyle = makeStyles((theme) => ({
  "@global": {
    "#nprogress": {
      pointerEvents: "none",
      "& .bar": {
        top: 0,
        left: 0,
        height: 2,
        width: "100%",
        position: "fixed",
        zIndex: theme.zIndex.snackbar,
        backgroundColor: theme.palette.primary.main,
        boxShadow: `0 0 2px ${theme.palette.primary.main}`,
      },
      "& .peg": {
        right: 0,
        opacity: 1,
        width: 100,
        height: "100%",
        display: "block",
        position: "absolute",
        transform: "rotate(3deg) translate(0px, -4px)",
        boxShadow: `0 0 10px ${theme.palette.primary.main}, 0 0 5px ${theme.palette.primary.main}`,
      },
    },
  },
}));

function RouteProgress(props) {
  nprogressStyle();

  NProgress.configure({
    speed: 500,
    showSpinner: false,
  });

  useEffect(() => {
    NProgress.done();
    return () => {
      NProgress.start();
    };
  }, []);

  return <Route {...props} />;
}

export function RenderRoutes(routes = []) {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, i) => {
          const Component = route.component;
          const Guard = route.guard || Fragment;
          const Layout = route.layout || Fragment;
          const role = route.role || [];
          // F-role-gates · 2026-08-25 · Envolvemos el component con
          // <RequireAction> si su path está registrado en `actionByRoute`.
          // El guard usa `useCanDo` — admin dueño (sin sesión empleado
          // firmada) pasa siempre; empleado con PIN es validado contra
          // sus acciones. Con esto cerramos el hueco de "entrar por URL
          // directa" a secciones que el nav ya ocultaba.
          const gate = route.path ? getRouteGate(route.path) : null;

          return (
            <RouteProgress
              key={i}
              path={route.path}
              exact={route.exact}
              render={(props) => (
                <RoleBasedGuard accessibleRoles={role}>
                  <Guard>
                    <Layout>
                      {route.routes ? (
                        RenderRoutes(route.routes)
                      ) : gate ? (
                        <RequireAction
                          action={gate.action}
                          mode={gate.mode}
                          sectionLabel={gate.label}
                        >
                          <Component {...props} />
                        </RequireAction>
                      ) : (
                        <Component {...props} />
                      )}
                    </Layout>
                  </Guard>
                </RoleBasedGuard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  );
}

const routes = [
  // Others Routes
  {
    exact: true,
    guard: GuestProtect,
    path: PATH_PAGE.root,
    role: [],
    component: () => <Redirect to={PATH_APP.tuCarta.root} />,
  },
  {
    exact: true,
    guard: GuestProtect,
    path: PATH_PAGE.auth.login,
    role: [],
    component: lazy(() => import("src/views/auth/LoginView")),
  },
  {
    exact: true,
    guard: GuestProtect,
    path: PATH_PAGE.auth.loginEmployee,
    role: [],
    component: lazy(() => import("src/views/auth/LoginEmployee")),
  },
  {
    exact: true,
    role: [],
    path: PATH_PAGE.auth.resetPassword,
    component: lazy(() => import("src/views/auth/ResetPasswordView")),
  },
  {
    exact: true,
    role: [],
    path: PATH_PAGE.auth.verify,
    component: lazy(() => import("src/views/auth/VerifyCodeView")),
  },
  // Bloque A: verificación OTP tras registro público en landing. Sin
  // guard — el user aún NO tiene JWT (se emite al verificar).
  {
    exact: true,
    role: [],
    path: PATH_PAGE.auth.verifyOtp,
    component: lazy(() => import("src/views/auth/VerifyOtpView")),
  },
  // Placeholder wizard onboarding (Bloque B). AuthProtect: solo user
  // logueado tras VerifyOtpView aterriza aquí. Se rellena en sprints B.
  {
    exact: true,
    guard: AuthProtect,
    role: [],
    path: PATH_PAGE.wizard,
    component: lazy(() => import("src/views/onboarding/WizardPlaceholder")),
  },
  {
    exact: true,
    path: "/404",
    role: [],
    component: lazy(() => import("src/views/errors/Page404View")),
  },
  {
    exact: true,
    path: "/500",
    component: lazy(() => import("src/views/errors/Page500View")),
  },
  {
    exact: true,
    guard: NoAuthorizedSessionProtect,
    role: [],
    path: PATH_PAGE.start,
    layout: HomeLayout,
    component: lazy(() =>
      import("src/views/app/UserNormal/cartaPrivada/tucarta/StartSession")
    ),
  },
  {
    exact: true,
    guard: NoAuthorizedSessionProtect,
    role: [],
    path: PATH_PAGE.dynamicSession,
    layout: HomeLayout,
    component: lazy(() =>
      import("src/views/app/UserNormal/cartaPrivada/tucarta/StartDynamiSession")
    ),
  },
  {
    exact: true,
    role: [],
    path: PATH_HOME.tucarta.successPayment,
    component: lazy(() =>
      import("src/views/app/UserNormal/StripeIntegration/paymentConfirmation")
    ),
  },
  {
    // Reserva online pública por slug — sin auth, sin layout (la página es
    // standalone para clientes finales del restaurante).
    exact: true,
    role: [],
    path: PATH_PAGE.publicBooking,
    component: lazy(() => import("src/views/public/PublicBooking")),
  },
  {
    exact: true,
    role: [],
    path: PATH_PAGE.publicMenu,
    component: lazy(() => import("src/views/public/PublicCarta")),
  },
  {
    // Vista pública "Gestionar mi reserva" — entrada directa desde los
    // correos/WhatsApp con code+contact en query params. Solo consulta;
    // el icono superior derecho devuelve al wizard de nueva reserva.
    exact: true,
    role: [],
    path: PATH_PAGE.publicBookingManage,
    component: lazy(() => import("src/views/public/PublicBookingManage")),
  },
  {
    // Vista pública de oferta auto-waitlist. El cliente abre el link del
    // email, ve el countdown y confirma. Sin auth.
    exact: true,
    role: [],
    path: PATH_PAGE.publicWaitlistOffer,
    component: lazy(() => import("src/views/public/PublicWaitlistOffer")),
  },
  {
    // F6: factura pública por QR. El cliente final escanea el QR del
    // ticket, accede a /factura/{slug} y completa sus datos fiscales
    // para que se genere la factura nominativa. Single-use; tras canjear
    // queda inutilizado.
    exact: true,
    role: [],
    path: PATH_PAGE.publicInvoice,
    component: lazy(() => import("src/views/public/PublicInvoice")),
  },
  {
    // Firma diferida de fichaje. El trabajador llega aquí desde el email
    // que envió el sistema cuando el admin solicitó modificar su fichaje
    // sin tenerlo presente. Sin auth — el token (256 bits hex en la URL)
    // es la credencial; vence a los 7 días, single-use.
    exact: true,
    role: [],
    path: PATH_PAGE.signClocking,
    component: lazy(() => import("src/views/public/SignClocking")),
  },
  {
    // Cerrar fichaje olvidado desde el enlace del email de recordatorio.
    // Sin auth — token Guid en la URL; TTL 2·IntervalMinutes; single-use
    // (se invalida al confirmar el cierre).
    exact: true,
    role: [],
    path: PATH_PAGE.closeForgottenShift,
    component: lazy(() => import("src/views/public/CloseForgottenShift")),
  },
  {
    // Portal del trabajador. Sin auth — token largo en la URL.
    // Multi-uso hasta vencer / ser revocado por el admin.
    exact: true,
    role: [],
    path: PATH_PAGE.employeePortal,
    component: lazy(() => import("src/views/public/EmployeePortal")),
  },
  {
    exact: true,
    guard: GuestProtect,
    role: [],
    path: PATH_APP.root,
    component: () => <Redirect to={PATH_APP.tuCarta.root} />,
  },
  UserRoutes,
  AdminRoutes,
  AppRoutes,
  TpvCartaRoutes,
  TpvRoutes,
  SessionRoutes,
  {
    component: () => <Redirect to="/404" />,
  },
];

export default routes;
